import type { Locale, Me } from '@nexo/types';
import { useEffect, useRef } from 'react';
import { useAuth } from '../features/auth/AuthProvider';
import { api } from './api';
import { currentLanguage, onLanguage, setLanguage } from './i18n';

const STORAGE_KEY = 'nexo.lang';

/** Whether the interface language was chosen on this device (see i18n.ts). */
function chosenOnThisDevice(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Keeps the interface language and the account language (`Me.locale`, used for emails) in step:
 * - on sign-in, a device without its own choice adopts the account's language;
 * - changing the language afterwards saves it to the account.
 * Renders nothing; mount it inside <AuthProvider>.
 */
export function AccountLanguageSync(): null {
  const { user, setUser } = useAuth();
  const userRef = useRef<Me | null>(user);
  const setUserRef = useRef(setUser);
  /** Account whose language has already been applied on this load. */
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    userRef.current = user;
    setUserRef.current = setUser;
  });

  // (a) First load of an account: follow its language unless this device has its own choice.
  useEffect(() => {
    if (!user) {
      appliedFor.current = null;
      return;
    }
    if (appliedFor.current === user.id) return;
    appliedFor.current = user.id;
    if (!chosenOnThisDevice() && user.locale !== currentLanguage()) setLanguage(user.locale);
  }, [user]);

  // (b) Later changes of the interface language are saved to the account.
  useEffect(() => {
    let previous: Locale | null = null;
    const unsubscribe = onLanguage((language) => {
      // onLanguage calls back immediately with the current language: not a change.
      const first = previous === null;
      previous = language;
      if (first) return;
      const me = userRef.current;
      if (!me || me.locale === language) return;
      api.users.updateMe({ locale: language }).then(
        (updated) => setUserRef.current(updated),
        () => undefined,
      );
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return null;
}
